import { useState, useCallback, useEffect, useMemo  } from "react";
import { Outlet } from 'react-router-dom';
import Sidebar from "./sidebar/Sidebar";
import Settings from './Settings';
import { SettingsBtn, SessionTimeoutWarning } from "../components/ui";
import { useAuthContext } from '../contexts/useAuthContext';
import NavBar from './Navbar';
import TreeRefreshContext from '../contexts/TreeRefreshContext';
import { AlarmSoundProvider } from '../contexts/AlarmSoundContext';
import { BureauSignalRProvider } from '../contexts/BureauSignalRContext';
import useSiteTree from '../hooks/useSiteTree';


const SidebarLayout = () => {
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [bureauMode, setBureauMode] = useState(true);
        const MD_BREAKPOINT = 768;
        const [isCollapsedByBreakpoint, setIsCollapsedByBreakpoint] = useState<boolean>(() =>
            typeof window !== 'undefined' && window.innerWidth < MD_BREAKPOINT
        );
    const { nodes, loading, error, refresh } = useSiteTree();
    const auth = useAuthContext();

    const handleOpenSettings = () => {
      setIsSettingsOpen(true); 
  };

    const handleCloseSettings = () => {
        setIsSettingsOpen(false);
    };

    const handleToggleSidebar = () => setSidebarCollapsed(c => !c);

        useEffect(() => {
            if (typeof window === 'undefined') {
                return undefined;
            }

            const mq = window.matchMedia(`(max-width: ${MD_BREAKPOINT - 1}px)`);

            const handler = (ev: MediaQueryListEvent) => {
                setIsCollapsedByBreakpoint(ev.matches);
            };

            setIsCollapsedByBreakpoint(mq.matches);

            mq.addEventListener('change', handler);

            return () => {
                mq.removeEventListener('change', handler);
            };
        }, []);

    // Memoize the refresh function for the context - make it properly async
    const refreshTree = useCallback(async () => {
        await refresh();
    }, [refresh]);

    // Key to trigger CSH-specific sidebar refreshes (for group/customer/site changes)
    const [cshRefreshKey, setCshRefreshKey] = useState<number>(0);
    const refreshCsh = useCallback(() => {
        setCshRefreshKey(k => k + 1);
    }, []);

    const TreeRefreshContextValue = useMemo(() => ({
        refreshTree,
        treeNodes: nodes,
        treeLoading: loading,
        treeError: error,
        bureauMode,
        setBureauMode,
        refreshCsh,
        cshRefreshKey
    }), [
        refreshTree,
        nodes,
        loading,
        error,
        bureauMode,
        refreshCsh,
        cshRefreshKey
    ]);

    return (
        <>
            <header>
                <NavBar onToggleSidebar={handleToggleSidebar} />
            </header>
            <BureauSignalRProvider>
            <AlarmSoundProvider>
                    <TreeRefreshContext.Provider value={TreeRefreshContextValue}>
                    <div className={`app-shell ${ (sidebarCollapsed || isCollapsedByBreakpoint) ? 'sidebar-collapsed' : ''}`}>
                        <Sidebar collapsed={ (sidebarCollapsed || isCollapsedByBreakpoint) } />
                        <div className="app-content">
                          <Outlet />
                        </div>
                        <SessionTimeoutWarning />
                        {/* Settings panel visible to admin and support users (not normal users) */}
                        {(auth?.isAdmin() || !auth?.isNormalUser()) && auth?.isAuthenticated && (
                            <>
                                <SettingsBtn onOpen={handleOpenSettings} />
                                <Settings isOpen={isSettingsOpen} onClose={handleCloseSettings} />
                            </>
                        )}
                    </div>
                </TreeRefreshContext.Provider>
            </AlarmSoundProvider>
            </BureauSignalRProvider>
        </>
    );
};

export default SidebarLayout;
